import type {
  HomeFeaturedMasterItem,
  HomeNewMasterItem,
  PublicMasterProfile,
} from '../../types/auth';

export type HomeMasterCardLanguage = 'ru' | 'en' | 'es';

type HomeMasterCardMetaSource = {
  primaryDirection?: string | null;
  shortDescription?: string | null;
};

export type HomeMasterCardMeta = {
  kind: 'direction' | 'description' | 'fallback';
  text: string;
};

const DIRECTION_LABELS: Record<string, Record<HomeMasterCardLanguage, string>> = {
  'Медитация': { ru: 'Медитация', en: 'Meditation', es: 'Meditación' },
  'Психология': { ru: 'Психология', en: 'Psychology', es: 'Psicología' },
  'Философия': { ru: 'Философия', en: 'Philosophy', es: 'Filosofía' },
  'Телесные практики': { ru: 'Телесные практики', en: 'Body practices', es: 'Prácticas corporales' },
  'Дыхательные практики': { ru: 'Дыхательные практики', en: 'Breathwork', es: 'Prácticas de respiración' },
  'Утренняя практика': { ru: 'Утренняя практика', en: 'Morning practice', es: 'Práctica matutina' },
  'Перед сном': { ru: 'Перед сном', en: 'Before sleep', es: 'Antes de dormir' },
  'Традиции': { ru: 'Традиции', en: 'Traditions', es: 'Tradiciones' },
  'Здоровое тело': { ru: 'Здоровое тело', en: 'Healthy body', es: 'Cuerpo sano' },
  'Музыка': { ru: 'Музыка', en: 'Music', es: 'Música' },
  'Искусство': { ru: 'Искусство', en: 'Art', es: 'Arte' },
  'Йога': { ru: 'Йога', en: 'Yoga', es: 'Yoga' },
  'Эмоциональный интеллект': { ru: 'Эмоциональный интеллект', en: 'Emotional intelligence', es: 'Inteligencia emocional' },
  'Литература': { ru: 'Литература', en: 'Literature', es: 'Literatura' },
  'Питание': { ru: 'Питание', en: 'Nutrition', es: 'Nutrición' },
  'Долголетие': { ru: 'Долголетие', en: 'Longevity', es: 'Longevidad' },
  'Короткая практика': { ru: 'Короткая практика', en: 'Short practice', es: 'Práctica corta' },
};

const LEGACY_SPECIALTY_LABELS: Record<string, Record<HomeMasterCardLanguage, string>> = {
  yoga: DIRECTION_LABELS['Йога'],
  meditation: DIRECTION_LABELS['Медитация'],
  psychology: DIRECTION_LABELS['Психология'],
  breathwork: DIRECTION_LABELS['Дыхательные практики'],
  qigong: { ru: 'Цигун', en: 'Qigong', es: 'Qigong' },
  mindfulness: { ru: 'Осознанность', en: 'Mindfulness', es: 'Mindfulness' },
  relaxation: { ru: 'Релаксация', en: 'Relaxation', es: 'Relajación' },
  wellness: { ru: 'Велнес', en: 'Wellness', es: 'Bienestar' },
  buddhism: { ru: 'Буддизм', en: 'Buddhism', es: 'Budismo' },
  shaivism: { ru: 'Шайвизм', en: 'Shaivism', es: 'Shivaísmo' },
};

const normalizeProfileText = (value: string | null | undefined): string =>
  String(value || '').replace(/\s+/g, ' ').trim();

const localizeDirection = (value: string, language: HomeMasterCardLanguage): string => {
  const normalized = normalizeProfileText(value);
  const canonical = DIRECTION_LABELS[normalized];
  if (canonical) return canonical[language];

  const legacy = LEGACY_SPECIALTY_LABELS[normalized.toLowerCase()];
  return legacy ? legacy[language] : normalized;
};

const profileSpecialization = (
  master: PublicMasterProfile,
  language: HomeMasterCardLanguage,
): string | null => {
  const structured = (master.directions || [])
    .map(direction => localizeDirection(direction, language))
    .filter(Boolean);
  const legacy = structured.length > 0
    ? []
    : (master.specialties || [])
      .map(specialty => localizeDirection(specialty, language))
      .filter(Boolean);
  const labels = (structured.length > 0 ? structured : legacy).slice(0, 2);
  return labels.length > 0 ? labels.join(' · ') : null;
};

const compactDescription = (value: string): string => {
  if (value.length <= 160) return value;
  const clipped = value.slice(0, 159).trimEnd();
  const lastSpace = clipped.lastIndexOf(' ');
  const safeEnd = lastSpace >= 112 ? clipped.slice(0, lastSpace) : clipped;
  return `${safeEnd}…`;
};

/**
 * Prefer the structured specialization supplied by the home BFF. Older/current
 * responses can leave it empty even though the published profile description
 * is present, so use that real profile copy rather than leaving a blank card.
 */
export function resolveHomeMasterCardMeta(
  master: HomeMasterCardMetaSource,
  fallbackLabel: string,
): HomeMasterCardMeta {
  const direction = normalizeProfileText(master.primaryDirection);
  if (direction) return { kind: 'direction', text: direction };

  const description = normalizeProfileText(master.shortDescription);
  if (description.length >= 4) {
    return { kind: 'description', text: compactDescription(description) };
  }

  return { kind: 'fallback', text: fallbackLabel };
}

export function formatHomeMasterCardMeta(
  meta: HomeMasterCardMeta,
  roleLabel: string,
): string {
  return meta.kind === 'fallback' ? roleLabel : `${roleLabel} · ${meta.text}`;
}

export function publicMasterToHomeMaster(
  master: PublicMasterProfile,
  language: HomeMasterCardLanguage = 'ru',
): HomeNewMasterItem {
  return {
    id: master.id,
    displayName: master.name,
    avatarUrl: master.photoUrl,
    // CORE-1384, возврат QA 03.10 — эта ветка строит карточку «Популярных
    // мастеров», когда `/api/home` отдал подборку пустой. Раньше здесь
    // оставался только оригинал, и карточка грузила 68 КБ вместо 7 КБ: тот же
    // мастер на том же экране выглядел по-разному, смотря каким путём пришёл.
    avatarVariants: master.photoVariants ?? null,
    // Выдача старше agape-backend!728 набора не отдаёт, но превью у неё есть.
    // Пусть одно известное превью и будет ответом: «нет набора» — это не
    // «превью нет вовсе», а только второе даёт право на оригинал.
    avatarPreviewUrl: master.photoThumbUrl ?? null,
    primaryDirection: profileSpecialization(master, language),
    shortDescription: master.description || master.intention || null,
    profileUrl: master.userId ? `/u/${master.userId}` : `/masters/${master.id}`,
    profileCompleteness: 100,
    publicationStatus: 'published',
    publishedAt: null,
  };
}

/**
 * Keep BFF membership/order intact and only enrich missing card metadata from
 * the corresponding public profile. A ranked profile never turns into a new
 * master merely because it was returned by the directory endpoint.
 */
export function enrichHomeMastersWithProfiles<T extends HomeFeaturedMasterItem | HomeNewMasterItem>(
  masters: readonly T[],
  profiles: readonly PublicMasterProfile[],
  language: HomeMasterCardLanguage = 'ru',
): T[] {
  const profilesById = new Map(profiles.map(profile => [profile.id, profile]));

  return masters.map((master) => {
    if (normalizeProfileText(master.primaryDirection)) return master;
    const profile = profilesById.get(master.id);
    if (!profile) return master;
    const primaryDirection = profileSpecialization(profile, language);
    return primaryDirection ? { ...master, primaryDirection } : master;
  });
}

/**
 * Curated BFF data remains authoritative. The ranked public directory is only
 * a fallback for environments where the enabled featured block is empty.
 */
export function resolveFeaturedHomeMasters(
  curated: readonly HomeFeaturedMasterItem[],
  ranked: readonly PublicMasterProfile[],
  language: HomeMasterCardLanguage = 'ru',
  fallbackLimit = 4,
): Array<HomeFeaturedMasterItem | HomeNewMasterItem> {
  const source: Array<HomeFeaturedMasterItem | HomeNewMasterItem> = curated.length > 0
    ? enrichHomeMastersWithProfiles(curated, ranked, language)
    : ranked.map(master => publicMasterToHomeMaster(master, language)).slice(0, fallbackLimit);
  const seen = new Set<number>();

  return source.filter((master) => {
    if (seen.has(master.id)) return false;
    seen.add(master.id);
    return true;
  });
}
